import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  getPlugin,
  savePluginSettings,
  togglePlugin,
  testMetaEvent,
} from '../../store/AsyncMethods/PluginsMethod';
import { RESET_PLUGINS_MSG } from '../../store/Types/PluginsTypes';

const META_EVENTS = ['PageView', 'ViewContent', 'AddToCart', 'InitiateCheckout', 'Purchase'];

const Field = ({ label, id, type = 'text', value, onChange, placeholder, helpText, required }) => (
  <div>
    <label htmlFor={id} className="block text-sm font-medium text-gray-700 mb-1">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    <input
      id={id}
      type={type}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      className="w-full px-3.5 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent shadow-sm"
    />
    {helpText && <p className="mt-1 text-xs text-gray-400">{helpText}</p>}
  </div>
);

const Section = ({ title, children }) => (
  <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 space-y-5">
    <h2 className="text-base font-semibold text-gray-800 border-b border-gray-100 pb-3">{title}</h2>
    {children}
  </div>
);

export default function MetaAdsPlugin() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { currentPlugin, loading, success, error } = useSelector((s) => s.PluginsReducer);

  const [pixelId, setPixelId] = useState('');
  const [accessToken, setAccessToken] = useState('');
  const [catalogId, setCatalogId] = useState('');
  const [testEvent, setTestEvent] = useState('PageView');
  const [testCode, setTestCode] = useState('');
  const [testResult, setTestResult] = useState(null);
  const [testLoading, setTestLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toggling, setToggling] = useState(false);

  useEffect(() => {
    dispatch(getPlugin('meta_ads'));
  }, []);

  useEffect(() => {
    if (currentPlugin?.plugin_key === 'meta_ads') {
      setPixelId(currentPlugin.settings?.pixel_id || '');
      setAccessToken(currentPlugin.settings?.access_token || '');
      setCatalogId(currentPlugin.settings?.catalog_id || '');
    }
  }, [currentPlugin]);

  useEffect(() => {
    if (success) { toast.success(success); dispatch({ type: RESET_PLUGINS_MSG }); setSaving(false); setToggling(false); }
    if (error) { toast.error(error); dispatch({ type: RESET_PLUGINS_MSG }); setSaving(false); setToggling(false); }
  }, [success, error]);

  const handleSave = async () => {
    if (!pixelId.trim()) { toast.error('Pixel ID is required'); return; }
    setSaving(true);
    dispatch(savePluginSettings('meta_ads', {
      pixel_id: pixelId.trim(),
      access_token: accessToken.trim(),
      catalog_id: catalogId.trim(),
    }));
  };

  const handleToggle = () => {
    setToggling(true);
    dispatch(togglePlugin('meta_ads'));
  };

  const handleTestEvent = async () => {
    if (!pixelId.trim() || !accessToken.trim()) {
      toast.error('Save Pixel ID and Access Token first');
      return;
    }
    setTestLoading(true);
    setTestResult(null);
    const result = await dispatch(testMetaEvent(testEvent, testCode.trim() || undefined));
    setTestResult(result);
    setTestLoading(false);
  };

  const isActive = currentPlugin?.is_active;

  return (
    <div className="mx-3 pb-10 max-w-3xl">
      {/* Back + Header */}
      <div className="flex items-center gap-3 mb-6">
        <button
          onClick={() => navigate('/plugins')}
          className="p-2 rounded-xl hover:bg-gray-100 transition-colors text-gray-500"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <div className="flex items-center gap-3 flex-1">
          <div className="w-10 h-10 rounded-xl bg-blue-600 flex items-center justify-center shadow">
            <svg viewBox="0 0 24 24" className="w-6 h-6 fill-white">
              <path d="M12 2C6.477 2 2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.879V14.89h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.989C18.343 21.129 22 16.99 22 12c0-5.523-4.477-10-10-10z" />
            </svg>
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-800">Meta Ads</h1>
            <p className="text-sm text-gray-400">Facebook & Instagram advertising integration</p>
          </div>
        </div>
        {/* Active toggle */}
        <div className="flex items-center gap-2.5">
          <span className={`text-sm font-medium ${isActive ? 'text-green-600' : 'text-gray-400'}`}>
            {isActive ? 'Active' : 'Inactive'}
          </span>
          <button
            onClick={handleToggle}
            disabled={toggling || loading}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 focus:outline-none disabled:opacity-50 ${
              isActive ? 'bg-green-500' : 'bg-gray-300'
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform duration-200 ${
                isActive ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>
      </div>

      <div className="space-y-5">
        {/* Credentials */}
        <Section title="Credentials">
          <Field
            label="Meta Pixel ID"
            id="pixel_id"
            value={pixelId}
            onChange={(e) => setPixelId(e.target.value)}
            placeholder="e.g. 123456789012345"
            helpText="Found in Meta Events Manager → Data Sources → your Pixel."
            required
          />
          <Field
            label="Access Token (Conversions API)"
            id="access_token"
            type="password"
            value={accessToken}
            onChange={(e) => setAccessToken(e.target.value)}
            placeholder="EAAxxxxxxxxxxxxxxxx..."
            helpText="Generate in Meta Events Manager → Settings → Conversions API → Generate access token. Required for server-side tracking."
          />
          <Field
            label="Catalog ID (optional)"
            id="catalog_id"
            value={catalogId}
            onChange={(e) => setCatalogId(e.target.value)}
            placeholder="e.g. 987654321012345"
            helpText="Your product catalog ID from Meta Commerce Manager. Enables dynamic product ads."
          />
          <button
            onClick={handleSave}
            disabled={saving || loading}
            className="mt-2 w-full py-2.5 px-4 rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {saving ? (
              <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div> Saving...</>
            ) : (
              'Save Settings'
            )}
          </button>
        </Section>

        {/* Events fired */}
        <Section title="Tracked Events">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {[
              { name: 'ViewContent', icon: '👁️', desc: 'Product detail page visited', trigger: 'Browser + Server' },
              { name: 'AddToCart', icon: '🛒', desc: 'Item added to cart', trigger: 'Browser + Server' },
              { name: 'InitiateCheckout', icon: '💳', desc: 'Checkout page opened', trigger: 'Browser' },
              { name: 'Purchase', icon: '✅', desc: 'Order completed successfully', trigger: 'Browser + Server' },
            ].map((ev) => (
              <div key={ev.name} className="flex items-start gap-3 p-3.5 rounded-xl bg-gray-50 border border-gray-100">
                <span className="text-xl mt-0.5">{ev.icon}</span>
                <div>
                  <p className="text-sm font-semibold text-gray-700">{ev.name}</p>
                  <p className="text-xs text-gray-400 mt-0.5">{ev.desc}</p>
                  <span className="inline-block mt-1 text-xs px-2 py-0.5 rounded-full bg-blue-50 text-blue-500 font-medium">
                    {ev.trigger}
                  </span>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-2 p-3.5 rounded-xl bg-amber-50 border border-amber-100 text-xs text-amber-700 leading-relaxed">
            <strong>Deduplication:</strong> Each event carries a unique <code>event_id</code> shared between browser pixel and CAPI, so Meta deduplicates automatically. Product IDs sent match your catalog's <code>retailer_id</code>.
          </div>
        </Section>

        {/* Test Events */}
        <Section title="Test Events">
          <p className="text-sm text-gray-500">
            Send a test event to Meta's Conversions API to verify your setup. Open{' '}
            <a
              href="https://business.facebook.com/events_manager"
              target="_blank"
              rel="noreferrer"
              className="text-blue-500 underline"
            >
              Meta Events Manager
            </a>{' '}
            → Test Events tab to see incoming events in real time.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Event Name</label>
              <select
                value={testEvent}
                onChange={(e) => setTestEvent(e.target.value)}
                className="w-full px-3.5 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 shadow-sm bg-white"
              >
                {META_EVENTS.map((ev) => (
                  <option key={ev} value={ev}>{ev}</option>
                ))}
              </select>
            </div>
            <Field
              label="Test Event Code (optional)"
              id="test_code"
              value={testCode}
              onChange={(e) => setTestCode(e.target.value)}
              placeholder="TEST12345"
              helpText="From Meta Events Manager → Test Events."
            />
          </div>
          <button
            onClick={handleTestEvent}
            disabled={testLoading || !pixelId || !accessToken}
            className="w-full py-2.5 px-4 rounded-xl bg-gray-800 hover:bg-gray-900 text-white text-sm font-semibold transition-colors disabled:opacity-40 flex items-center justify-center gap-2"
          >
            {testLoading ? (
              <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div> Sending...</>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                Send Test Event
              </>
            )}
          </button>
          {testResult && (
            <div className={`rounded-xl p-4 text-xs font-mono whitespace-pre-wrap break-all ${
              testResult.meta_response?.events_received > 0
                ? 'bg-green-50 border border-green-100 text-green-800'
                : 'bg-red-50 border border-red-100 text-red-800'
            }`}>
              {JSON.stringify(testResult.meta_response || testResult, null, 2)}
            </div>
          )}
        </Section>

        {/* Setup Guide */}
        <Section title="Setup Instructions">
          <ol className="space-y-3 text-sm text-gray-600 list-none">
            {[
              { step: '1', text: 'Go to Meta Business Suite → Events Manager → Data Sources → Add new data source → Web.' },
              { step: '2', text: 'Copy the Pixel ID and paste it in the Credentials section above.' },
              { step: '3', text: 'In Events Manager → Settings → Conversions API → Generate access token. Paste it above.' },
              { step: '4', text: '(Optional) Create a product catalog in Meta Commerce Manager. Copy the Catalog ID.' },
              { step: '5', text: 'Click Save Settings, then activate the toggle to enable tracking.' },
              { step: '6', text: 'Use the Test Events section to verify that Meta is receiving events.' },
            ].map(({ step, text }) => (
              <li key={step} className="flex gap-3">
                <span className="shrink-0 w-6 h-6 rounded-full bg-blue-100 text-blue-700 text-xs font-bold flex items-center justify-center mt-0.5">
                  {step}
                </span>
                <span>{text}</span>
              </li>
            ))}
          </ol>
          <div className="mt-3 p-3.5 rounded-xl bg-blue-50 border border-blue-100 text-xs text-blue-700 leading-relaxed">
            <strong>Dual tracking:</strong> This plugin fires events both via the browser (Meta Pixel JS) and server-side (Conversions API). Server-side tracking improves accuracy when browsers block pixels (iOS 14+, ad blockers).
          </div>
        </Section>
      </div>
    </div>
  );
}
